import { useRef, useEffect } from "react";
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading2,
  Heading3,
  Pilcrow,
  Image as ImageIcon,
  Loader2,
  Link2,
  Link2Off,
  Palette,
} from "lucide-react";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { handbookUploadImage } from "@/lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const COLORS = ["#0f172a", "#dc2626", "#ea580c", "#16a34a", "#2563eb", "#7c3aed", "#0d9488", "#737373"];

function ToolBtn({ onClick, title, children, testId }) {
  return (
    <button
      type="button"
      title={title}
      data-testid={testId}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="h-8 w-8 inline-flex items-center justify-center rounded hover:bg-muted text-foreground/80"
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({ value, onChange, testId }) {
  const ref = useRef(null);
  const fileRef = useRef(null);
  const savedRange = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [hex, setHex] = useState("#0f172a");
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [imgSelected, setImgSelected] = useState(false);
  const [captionOpen, setCaptionOpen] = useState(false);
  const [captionText, setCaptionText] = useState("");
  const selImg = useRef(null);

  useEffect(() => {
    if (ref.current) ref.current.innerHTML = value || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = () => onChange?.(ref.current?.innerHTML || "");

  const exec = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    emit();
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0);
    }
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    if (savedRange.current) {
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const applyColor = (color) => {
    setColorOpen(false);
    ref.current?.focus();
    restoreSelection();
    document.execCommand("foreColor", false, color);
    emit();
  };

  const onEditorClick = (e) => {
    if (e.target?.tagName === "IMG") {
      selImg.current = e.target;
      setImgSelected(true);
      setCaptionOpen(false);
    } else if (imgSelected) {
      selImg.current = null;
      setImgSelected(false);
      setCaptionOpen(false);
    }
  };

  const openCaption = () => {
    const img = selImg.current;
    const cap = img?.closest("figure")?.querySelector("figcaption");
    setCaptionText(cap?.textContent || "");
    setCaptionOpen(true);
  };

  const applyCaption = () => {
    const img = selImg.current;
    if (!img || !ref.current?.contains(img)) return;
    const text = captionText.trim();
    let fig = img.closest("figure");
    if (!fig) {
      fig = document.createElement("figure");
      fig.style.margin = "8px 0";
      img.parentNode.insertBefore(fig, img);
      fig.appendChild(img);
    }
    let cap = fig.querySelector("figcaption");
    if (!text) {
      if (cap) cap.remove();
    } else {
      if (!cap) {
        cap = document.createElement("figcaption");
        fig.appendChild(cap);
      }
      cap.textContent = text;
    }
    emit();
    setCaptionOpen(false);
  };

  const styleImg = (apply) => {
    const img = selImg.current;
    if (!img || !ref.current?.contains(img)) return;
    apply(img);
    emit();
  };

  const imgWidth = (w) =>
    styleImg((img) => {
      img.style.width = w;
      img.style.height = "auto";
    });

  const imgAlign = (a) =>
    styleImg((img) => {
      img.style.float = "none";
      img.style.display = "block";
      if (a === "center") {
        img.style.marginLeft = "auto";
        img.style.marginRight = "auto";
      } else if (a === "left") {
        img.style.marginLeft = "0";
        img.style.marginRight = "auto";
      } else {
        img.style.marginLeft = "auto";
        img.style.marginRight = "0";
      }
    });

  const openLink = () => {
    saveSelection();
    setLinkUrl("");
    setLinkOpen(true);
  };

  const applyLink = () => {
    let url = linkUrl.trim();
    if (!url) return;
    if (!/^https?:\/\//i.test(url) && !url.startsWith("mailto:")) url = `https://${url}`;
    ref.current?.focus();
    restoreSelection();
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) {
      // Keine Auswahl: URL als Linktext einfügen
      document.execCommand("insertHTML", false, `<a href="${url}" target="_blank" rel="noopener">${url}</a>`);
    } else {
      document.execCommand("createLink", false, url);
    }
    emit();
    setLinkOpen(false);
  };

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await handbookUploadImage(file);
      const abs = url.startsWith("http") ? url : `${BACKEND}${url}`;
      ref.current?.focus();
      document.execCommand(
        "insertHTML",
        false,
        `<img src="${abs}" alt="Screenshot" style="max-width:100%;border-radius:6px;margin:8px 0" />`
      );
      emit();
    } catch {
      toast.error("Bild-Upload fehlgeschlagen.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="border border-border rounded-md overflow-hidden" data-testid={`${testId}-wrapper`}>
      <div className="flex flex-wrap items-center gap-1 border-b border-border bg-muted/40 px-2 py-1">
        <ToolBtn title="Fett" onClick={() => exec("bold")} testId="rte-bold">
          <Bold className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn title="Kursiv" onClick={() => exec("italic")} testId="rte-italic">
          <Italic className="h-4 w-4" />
        </ToolBtn>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolBtn title="Überschrift" onClick={() => exec("formatBlock", "h2")} testId="rte-h2">
          <Heading2 className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn title="Unterüberschrift" onClick={() => exec("formatBlock", "h3")} testId="rte-h3">
          <Heading3 className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn title="Absatz" onClick={() => exec("formatBlock", "p")} testId="rte-p">
          <Pilcrow className="h-4 w-4" />
        </ToolBtn>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolBtn title="Aufzählung" onClick={() => exec("insertUnorderedList")} testId="rte-ul">
          <List className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn title="Nummerierte Liste" onClick={() => exec("insertOrderedList")} testId="rte-ol">
          <ListOrdered className="h-4 w-4" />
        </ToolBtn>
        <span className="w-px h-5 bg-border mx-1" />
        <div className="relative">
          <ToolBtn
            title="Textfarbe"
            onClick={() => {
              saveSelection();
              setColorOpen((o) => !o);
            }}
            testId="rte-color"
          >
            <Palette className="h-4 w-4" />
          </ToolBtn>
          {colorOpen && (
            <div
              className="absolute z-20 mt-1 left-0 bg-white border border-border rounded-md shadow-md p-2 w-56"
              data-testid="rte-color-palette"
            >
              <div className="grid grid-cols-4 gap-1">
                {COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    title={c}
                    onClick={() => applyColor(c)}
                    data-testid={`rte-color-${c}`}
                    className="h-6 w-full rounded border border-border"
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
              <button
                type="button"
                onClick={() => applyColor("#0f172a")}
                data-testid="rte-color-default"
                className="mt-2 w-full text-xs rounded border border-border px-2 py-1 hover:bg-muted text-left"
              >
                Standardfarbe
              </button>
              <div className="mt-2 flex items-center gap-1">
                <input
                  type="color"
                  value={hex}
                  onChange={(e) => setHex(e.target.value)}
                  data-testid="rte-color-hex-picker"
                  className="h-7 w-9 rounded border border-border bg-white p-0.5 cursor-pointer"
                  title="Freie Farbwahl"
                />
                <Input
                  value={hex}
                  onChange={(e) => setHex(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyColor(hex))}
                  placeholder="#RRGGBB"
                  className="h-7 text-xs"
                  data-testid="rte-color-hex-input"
                />
                <Button size="sm" className="h-7 px-2" onClick={() => applyColor(hex)} data-testid="rte-color-hex-apply">
                  OK
                </Button>
              </div>
            </div>
          )}
        </div>
        <ToolBtn title="Link einfügen" onClick={openLink} testId="rte-link">
          <Link2 className="h-4 w-4" />
        </ToolBtn>
        <ToolBtn title="Link entfernen" onClick={() => exec("unlink")} testId="rte-unlink">
          <Link2Off className="h-4 w-4" />
        </ToolBtn>
        <span className="w-px h-5 bg-border mx-1" />
        <ToolBtn title="Screenshot/Bild einfügen" onClick={() => fileRef.current?.click()} testId="rte-image">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />}
        </ToolBtn>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={onPickImage}
          data-testid="rte-image-input"
        />
      </div>
      {linkOpen && (
        <div
          className="flex items-center gap-2 border-b border-border bg-muted/20 px-2 py-2"
          data-testid="rte-link-row"
        >
          <Input
            autoFocus
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyLink())}
            placeholder="https://… oder mailto:…"
            className="h-8 text-sm"
            data-testid="rte-link-input"
          />
          <Button size="sm" onClick={applyLink} data-testid="rte-link-apply">
            Einfügen
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setLinkOpen(false)} data-testid="rte-link-cancel">
            Abbrechen
          </Button>
        </div>
      )}
      {imgSelected && (
        <div
          className="flex flex-wrap items-center gap-2 border-b border-border bg-muted/20 px-2 py-2 text-xs"
          data-testid="rte-image-controls"
        >
          <span className="text-muted-foreground font-medium">Bild:</span>
          <button type="button" onClick={() => imgWidth("50%")} data-testid="rte-img-50" className="rounded border border-border px-2 py-1 hover:bg-muted">
            50 %
          </button>
          <button type="button" onClick={() => imgWidth("100%")} data-testid="rte-img-100" className="rounded border border-border px-2 py-1 hover:bg-muted">
            100 %
          </button>
          <span className="w-px h-4 bg-border" />
          <button type="button" onClick={() => imgAlign("left")} data-testid="rte-img-left" className="rounded border border-border px-2 py-1 hover:bg-muted">
            Links
          </button>
          <button type="button" onClick={() => imgAlign("center")} data-testid="rte-img-center" className="rounded border border-border px-2 py-1 hover:bg-muted">
            Zentriert
          </button>
          <button type="button" onClick={() => imgAlign("right")} data-testid="rte-img-right" className="rounded border border-border px-2 py-1 hover:bg-muted">
            Rechts
          </button>
          <span className="w-px h-4 bg-border" />
          <button type="button" onClick={openCaption} data-testid="rte-img-caption" className="rounded border border-border px-2 py-1 hover:bg-muted">
            Bildunterschrift
          </button>
        </div>
      )}
      {imgSelected && captionOpen && (
        <div
          className="flex items-center gap-2 border-b border-border bg-muted/20 px-2 py-2"
          data-testid="rte-caption-row"
        >
          <Input
            autoFocus
            value={captionText}
            onChange={(e) => setCaptionText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), applyCaption())}
            placeholder="Bildunterschrift (leer lassen zum Entfernen)"
            className="h-8 text-sm"
            data-testid="rte-caption-input"
          />
          <Button size="sm" onClick={applyCaption} data-testid="rte-caption-apply">
            Übernehmen
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setCaptionOpen(false)} data-testid="rte-caption-cancel">
            Abbrechen
          </Button>
        </div>
      )}
      <div
        ref={ref}
        contentEditable
        onInput={emit}
        onClick={onEditorClick}
        suppressContentEditableWarning
        data-testid={testId}
        className="handbook-prose min-h-[220px] max-h-[520px] overflow-y-auto p-4 text-sm focus:outline-none"
      />
    </div>
  );
}
